import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Icon, Label } from "semantic-ui-react";

import { ReactTable } from "components";
import { ImportLogModal } from "modules/DealersDashboard/components/DealersDashboardTable/components/DealerLocationsTable/components";
import { getHoursSinceImport } from "modules/DealersDashboard/utils";
import { PATHS } from "router/paths";

import type { Dealer, DealerLocation } from "models";

const columnHelper = createColumnHelper<DealerLocation>();

type DealerLocationsTableProps = {
  dealer: Dealer;
};

const STALE_IMPORT_HOURS = 2;
const isImportStale = (location: DealerLocation) => getHoursSinceImport(location.last_import_at) >= STALE_IMPORT_HOURS;

export const isDealerInvoicingEnabled = (dealer: Dealer) => !!dealer.exact_account_id && dealer.is_exact_invoicing_enabled;
export const isLocationInvoicingEnabled = (location: DealerLocation, dealer: Dealer) =>
  location.is_exact_invoicing_enabled && (!!location.exact_account_id || isDealerInvoicingEnabled(dealer));

export const DealerLocationsTable = ({ dealer }: DealerLocationsTableProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [locationForImportLog, setLocationForImportLog] = useState<DealerLocation | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "location",
        size: 250,
        header: () => <span className="no-wrap-text">{t("v8_location", "Location")}</span>,
        cell: ({ row: { original: location } }) => (
          <div className="name-container">
            <div className={location.active ? "active" : "inactive"} />
            <span className={`no-wrap-text ${isImportStale(location) ? "stale-import" : ""}`}>{location.name}</span>
          </div>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "importer_version",
        size: 250,
        header: () => <span className="no-wrap-text">{t("v8_importer_version", "Importer Version")}</span>,
        cell: ({ row: { original: location } }) => (
          <div className={`importer-container ${isImportStale(location) ? "stale-import" : ""}`}>
            <div
              className="pointer"
              title={t("v8_show_import_log", "Show import log")}
              onClick={e => {
                e.stopPropagation();
                setLocationForImportLog(location);
              }}
            >
              <Icon className="cloud arrow up" />
            </div>
            <span className="no-wrap-text">{location.importer_version}</span>
          </div>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "invoicing",
        size: 250,
        header: () => <span className="no-wrap-text">{t("v8_invoicing_enabled", "Invoicing Enabled")}</span>,
        cell: ({ row: { original: location } }) => {
          const isEnabled = isLocationInvoicingEnabled(location, dealer);

          return (
            <div className="invoicing-container">
              <Icon className={isEnabled ? "check circle" : "xmark circle"} color={isEnabled ? "green" : "red"} />
              {!!location.number_of_missing_exact_licenses && (
                <Label circular color="red">
                  {location.number_of_missing_exact_licenses}
                </Label>
              )}
            </div>
          );
        }
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        header: "",
        size: 60,
        cell: ({ row: { original } }) =>
          original.accept_network_metrics ? <Icon className="wifi" title={t("v8_wifi_metrics_collected", "Wi-Fi metrics are being collected")} /> : null
      })
    ],
    [t, dealer]
  );

  return (
    <>
      <ReactTable
        columns={columns}
        data={dealer.locations}
        onRowClick={(location: DealerLocation) => history.push(`${PATHS.DEALERS_DASHBOARD}/locations/${location.id}`)}
      />

      {locationForImportLog && (
        <ImportLogModal
          locationId={locationForImportLog.id}
          dealerName={dealer.name}
          locationName={locationForImportLog.name}
          onClose={() => setLocationForImportLog(null)}
        />
      )}
    </>
  );
};
